import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/**
 * Calculate intermediate points for a curved line between two coordinates.
 * Uses great circle approximation for realistic flight paths.
 * 
 * Math explanation:
 * - For short distances: Simple curved arc (looks nice, computationally cheap)
 * - For long distances: Great circle route with slight arc (realistic flight path)
 * - Great circle is the shortest path on a sphere (what planes actually fly)
 * - We add a small perpendicular offset to make it visually distinct from straight line
 */
function calculateCurvedPath(lat1, lon1, lat2, lon2, numPoints = 50) {
  const points = [];
  
  // Convert to radians
  const toRad = (deg) => deg * Math.PI / 180;
  const toDeg = (rad) => rad * 180 / Math.PI;
  
  const lat1Rad = toRad(lat1);
  const lon1Rad = toRad(lon1);
  const lat2Rad = toRad(lat2);
  const lon2Rad = toRad(lon2);
  
  // Calculate great circle distance using Haversine formula
  const dLon = lon2Rad - lon1Rad;
  const dLat = lat2Rad - lat1Rad;
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1Rad) * Math.cos(lat2Rad) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  // If very short distance, use simple arc
  if (c < 0.1) {
    // Simple arc for nearby airports
    const midLat = (lat1 + lat2) / 2;
    const midLon = (lon1 + lon2) / 2;
    const distance = Math.sqrt(Math.pow(lat2 - lat1, 2) + Math.pow(lon2 - lon1, 2));
    const offsetFactor = distance * 0.2;
    
    const dx = lon2 - lon1;
    const dy = lat2 - lat1;
    const perpLon = -dy * offsetFactor;
    const perpLat = dx * offsetFactor;
    
    const controlLat = midLat + perpLat;
    const controlLon = midLon + perpLon;
    
    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;
      const lat = Math.pow(1 - t, 2) * lat1 + 
                  2 * (1 - t) * t * controlLat + 
                  Math.pow(t, 2) * lat2;
      const lon = Math.pow(1 - t, 2) * lon1 + 
                  2 * (1 - t) * t * controlLon + 
                  Math.pow(t, 2) * lon2;
      points.push([lat, lon]);
    }
    return points;
  }
  
  // For longer distances, use great circle route with slight arc
  for (let i = 0; i <= numPoints; i++) {
    const f = i / numPoints;
    
    // Great circle interpolation using Slerp (Spherical Linear Interpolation)
    const A = Math.sin((1 - f) * c) / Math.sin(c);
    const B = Math.sin(f * c) / Math.sin(c);
    
    const x = A * Math.cos(lat1Rad) * Math.cos(lon1Rad) +
              B * Math.cos(lat2Rad) * Math.cos(lon2Rad);
    const y = A * Math.cos(lat1Rad) * Math.sin(lon1Rad) +
              B * Math.cos(lat2Rad) * Math.sin(lon2Rad);
    const z = A * Math.sin(lat1Rad) + B * Math.sin(lat2Rad);
    
    const lat = toDeg(Math.atan2(z, Math.sqrt(x * x + y * y)));
    const lon = toDeg(Math.atan2(y, x));
    
    // Add slight perpendicular offset for visual distinction
    // Use parabolic function (peaks at midpoint) for smooth arc
    const arcHeight = 0.15 * Math.sin(f * Math.PI); // 0 at ends, max at middle
    const offsetLat = lat + arcHeight * Math.cos(toRad(lon - lon1));
    
    points.push([offsetLat, lon]);
  }
  
  return points;
}

/**
 * Get color for route based on vote count
 */
function getRouteColor(voteCount) {
  if (voteCount > 10) return '#00ff88'; // Radar green - highly voted
  if (voteCount > 5) return '#00d4ff';  // Signal blue - moderately voted
  if (voteCount > 0) return '#ffaa00';  // Warning amber - some votes
  if (voteCount < 0) return '#ff3366';  // Danger red - negative votes
  return '#9ca3af'; // Muted gray - no votes
}

function Map({ routes, onRouteClick, selectedRoute }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const routeLayersRef = useRef([]);

  useEffect(() => {
    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [30, 0], // Center on world
        zoom: 2,
        minZoom: 2,
        maxZoom: 10,
        worldCopyJump: true,
      });

      // Add dark theme tile layer
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      }).addTo(mapInstanceRef.current);
    }

    // Clear existing route layers
    routeLayersRef.current.forEach(layer => {
      mapInstanceRef.current.removeLayer(layer);
    });
    routeLayersRef.current = [];

    // Add route lines
    if (routes && routes.length > 0) {
      routes.forEach(route => {
        if (!route.origin_airport || !route.destination_airport) return;

        const origin = route.origin_airport;
        const dest = route.destination_airport;
        
        // Calculate curved path
        const curvedPath = calculateCurvedPath(
          origin.lat,
          origin.lon,
          dest.lat,
          dest.lon
        );

        // Create polyline with color based on votes
        const color = getRouteColor(route.vote_count || 0);
        const isSelected = selectedRoute && selectedRoute.id === route.id;
        
        const polyline = L.polyline(curvedPath, {
          color: color,
          weight: isSelected ? 4 : 2,
          opacity: isSelected ? 1 : 0.7,
          smoothFactor: 1
        }).addTo(mapInstanceRef.current);

        // Add popup
        const popupContent = `
          <div class="popup-route">${origin.iata} → ${dest.iata}</div>
          <div>${origin.city} → ${dest.city}</div>
          <div class="popup-votes">Votes: ${route.vote_count || 0}</div>
        `;
        
        polyline.bindPopup(popupContent);
        
        // Click handler
        polyline.on('click', () => {
          if (onRouteClick) {
            onRouteClick(route);
          }
        });

        routeLayersRef.current.push(polyline);

        // Add markers for origin and destination
        const originMarker = L.circleMarker([origin.lat, origin.lon], {
          radius: 6,
          fillColor: color,
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        }).addTo(mapInstanceRef.current);

        originMarker.bindPopup(`
          <div class="popup-route">${origin.iata}</div>
          <div>${origin.name}</div>
          <div>${origin.city}, ${origin.country}</div>
        `);

        routeLayersRef.current.push(originMarker);

        const destMarker = L.circleMarker([dest.lat, dest.lon], {
          radius: 6,
          fillColor: color,
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        }).addTo(mapInstanceRef.current);

        destMarker.bindPopup(`
          <div class="popup-route">${dest.iata}</div>
          <div>${dest.name}</div>
          <div>${dest.city}, ${dest.country}</div>
        `);

        routeLayersRef.current.push(destMarker);
      });

      // Fit map to show all routes if there are any
      if (routeLayersRef.current.length > 0) {
        const group = L.featureGroup(routeLayersRef.current);
        mapInstanceRef.current.fitBounds(group.getBounds(), {
          padding: [50, 50]
        });
      }
    }

    return () => {
      // Cleanup on unmount
      if (mapInstanceRef.current) {
        routeLayersRef.current.forEach(layer => {
          mapInstanceRef.current.removeLayer(layer);
        });
      }
    };
  }, [routes, onRouteClick, selectedRoute]);

  return (
    <div className="map-container">
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}

export default Map;
